import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { useAuth } from '../context/AuthContext'
import { pocketbaseErrorMessage } from '../lib/pocketbaseErrorMessage'
import {
  COL_MACHINES,
  COL_PRODUCTION_REQUEST_LINES,
  COL_SCHEDULE_SLOTS,
} from '../config/collections'
import { PERMISSION_KEYS } from '../config/permissions'
import {
  availabilityPercent,
  formatHours,
  formatMachineOutput,
  groupMachinesByType,
} from '../lib/machines'
import { isLineOverdue } from '../lib/requestLineStatus'
import { formatQty, SCHEDULE_SLOT_STATUS_LABELS } from '../lib/scheduleCalculations'
import { pb } from '../pocketbase'
import type { MachineRecord, ProductionRequestLineRecord, ScheduleSlotRecord } from '../types/records'
import { btn, cardClass, cn } from '../lib/ui'

function todayRange(): { start: string; end: string; day: string } {
  const day = new Date().toISOString().slice(0, 10)
  return { start: `${day} 00:00:00`, end: `${day} 23:59:59`, day }
}

/** Упрощённая схема станка (бобинорезка / перемотка) — декоративная. */
function MachineSvg({ accent }: { accent: string }) {
  return (
    <svg
      className="aspect-[100/120] h-auto w-full max-w-[120px]"
      viewBox="0 0 100 120"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path d="M8 102 L92 102 L88 112 L12 112 Z" fill="#475569" opacity="0.9" />
      <rect x="14" y="88" width="72" height="14" rx="2" fill="#64748b" />
      <rect x="22" y="38" width="56" height="52" rx="4" fill="#94a3b8" stroke="#64748b" strokeWidth="1.5" />
      <circle cx="50" cy="48" r="14" fill="#e2e8f0" stroke={accent} strokeWidth="2.5" />
      <ellipse cx="50" cy="48" rx="10" ry="4" fill={accent} opacity="0.35" />
      <circle cx="50" cy="72" r="12" fill="#e2e8f0" stroke={accent} strokeWidth="2" />
      <circle cx="50" cy="94" r="9" fill="#e2e8f0" stroke="#64748b" strokeWidth="1.5" />
      <path d="M50 34 Q62 28 72 38 L72 52 Q62 58 50 52 Z" fill={accent} opacity="0.45" />
      <rect x="46" y="52" width="8" height="36" rx="2" fill={accent} opacity="0.25" />
      <circle cx="78" cy="50" r="4" fill="#22c55e" className="animate-led-pulse" />
    </svg>
  )
}

/** Дашборд по производству на `/` для вошедших пользователей. */
export function ProductionDashboardStub() {
  const { user, can } = useAuth()
  const [machines, setMachines] = useState<MachineRecord[]>([])
  const [todaySlots, setTodaySlots] = useState<ScheduleSlotRecord[]>([])
  const [overdueLines, setOverdueLines] = useState<ProductionRequestLineRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [scheduleLoading, setScheduleLoading] = useState(false)
  const [err, setErr] = useState('')

  const showProduction =
    can(PERMISSION_KEYS.PRODUCTS_READ) || can(PERMISSION_KEYS.CONSUMABLES_READ)
  const showProductionRequests = can(PERMISSION_KEYS.PRODUCTION_REQUESTS_READ)
  const showPlanning = can(PERMISSION_KEYS.PRODUCTION_SCHEDULE_READ)
  const showProdActions = showProduction || showProductionRequests

  const planFact = useMemo(() => {
    const planned = todaySlots.reduce((sum, s) => sum + (Number(s.plannedQuantity) || 0), 0)
    const actual = todaySlots.reduce((sum, s) => sum + (Number(s.actualQuantity) || 0), 0)
    const inProgress = todaySlots.filter((s) => s.status === 'in_progress').length
    const completed = todaySlots.filter((s) => s.status === 'completed').length
    return { planned, actual, inProgress, completed }
  }, [todaySlots])

  const load = useCallback(async () => {
    setErr('')
    setLoading(true)
    try {
      const list = await pb.collection(COL_MACHINES).getFullList<MachineRecord>({
        sort: 'type,sort,number',
      })
      setMachines(list)
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, 'Ошибка загрузки станков'))
      setMachines([])
    } finally {
      setLoading(false)
    }
  }, [])

  const loadScheduleSummary = useCallback(async () => {
    if (!showPlanning && !showProductionRequests) {
      setTodaySlots([])
      setOverdueLines([])
      return
    }
    setScheduleLoading(true)
    try {
      const { start, end } = todayRange()
      const tasks: Promise<void>[] = []

      if (showPlanning) {
        tasks.push(
          pb.collection(COL_SCHEDULE_SLOTS)
            .getFullList<ScheduleSlotRecord>({
              filter: `plannedStart <= "${end}" && plannedEnd >= "${start}" && status != "cancelled"`,
              expand: 'requestLine.product,machine',
              sort: 'plannedStart',
            })
            .then(setTodaySlots),
        )
      } else {
        setTodaySlots([])
      }

      if (showProductionRequests) {
        tasks.push(
          pb.collection(COL_PRODUCTION_REQUEST_LINES)
            .getFullList<ProductionRequestLineRecord>({
              filter: 'status = "pending" || status = "scheduled" || status = "in_progress"',
              expand: 'product',
              sort: 'plannedProduction',
            })
            .then((lines) => setOverdueLines(lines.filter((ln) => isLineOverdue(ln)))),
        )
      } else {
        setOverdueLines([])
      }

      await Promise.all(tasks)
    } catch {
      setTodaySlots([])
      setOverdueLines([])
    } finally {
      setScheduleLoading(false)
    }
  }, [showPlanning, showProductionRequests])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    void loadScheduleSummary()
  }, [loadScheduleSummary])

  const grouped = groupMachinesByType(machines)

  return (
    <div className="mx-auto max-w-[1080px] px-5 py-8 pb-12">
      <header className="mb-8">
        <h1 className="m-0 mb-2 text-[1.75rem] font-semibold tracking-tight text-su-text-strong">
          Производство
        </h1>
        <p className="mb-3 max-w-2xl leading-relaxed text-gray-600">
          Сводка по парку станков. Учёт продукции и комплектации — отдельный раздел; справочники и
          роли — в «Управлении».
        </p>
        <p className="mb-4 text-su-text">
          Вы вошли как <strong>{user?.email}</strong>
          {user?.roleName ? ` (${user.roleName})` : ''}.
        </p>
        <div className="flex flex-wrap gap-2">
          {showProduction && (
            <Link to="/production" className={btn('primary')}>
              Продукция и комплектация
            </Link>
          )}
          {showProductionRequests && (
            <Link
              to="/production-requests"
              className={btn(showProduction ? 'ghost' : 'primary')}
            >
              Заявки на производство
            </Link>
          )}
          {showPlanning && (
            <Link to="/planning/schedule" className={btn('ghost')}>
              График производства
            </Link>
          )}
          <Link to="/admin" className={btn(showProdActions ? 'ghost' : 'primary')}>
            Управление
          </Link>
        </div>
      </header>

      <section
        className={cn(cardClass, 'mb-6 px-5 py-5')}
        aria-labelledby="machine-load-heading"
      >
        <h2 id="machine-load-heading" className="m-0 mb-1.5 text-[1.15rem] font-semibold text-su-text-strong">
          Парк станков
        </h2>
        <p className="mb-4 max-w-2xl text-[0.86rem] leading-snug text-gray-500">
          Доступность — доля рабочего времени за сутки с учётом заложенных техработ.
        </p>

        {loading && <p className="text-sm text-gray-500">Загрузка станков…</p>}
        {err && <p className="text-sm text-red-600">{err}</p>}
        {!loading && !err && machines.length === 0 && (
          <p className="text-sm text-gray-500">
            Станки не настроены. Добавьте их в разделе{' '}
            <Link to="/admin/administration/machines" className="text-blue-700 underline">
              «Станки»
            </Link>{' '}
            администрирования.
          </p>
        )}

        {!loading && !err && grouped.length > 0 && (
          <div className="flex flex-col gap-7">
            {grouped.map((line) => (
              <div key={line.type}>
                <h3 className="mb-3 text-base font-semibold text-gray-900">
                  {line.label}{' '}
                  <span className="ml-1 rounded-md bg-blue-50 px-1.5 py-0.5 text-[0.88rem] font-semibold text-blue-700">
                    {line.items.length} шт.
                  </span>
                </h3>
                <div className="grid grid-cols-[repeat(auto-fill,minmax(168px,1fr))] gap-4">
                  {line.items.map((machine) => {
                    const availability = availabilityPercent(machine)
                    return (
                      <article
                        key={machine.id}
                        className="flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-gradient-to-b from-[#fafbfc] to-slate-100 shadow-[0_2px_8px_rgba(15,23,42,0.06)]"
                        style={{ '--machine-accent': line.accent } as React.CSSProperties}
                      >
                        <div className="flex items-center justify-center border-b border-slate-200 bg-gradient-to-b from-white to-slate-50 px-3 py-2.5 pb-1.5">
                          <MachineSvg accent={line.accent} />
                        </div>
                        <div className="flex flex-1 flex-col px-3.5 py-2.5 pb-3.5">
                          <p className="m-0 text-[0.78rem] font-semibold uppercase tracking-wide text-slate-500">
                            {line.label}
                          </p>
                          <p className="mb-1 mt-0.5 text-[0.92rem] font-semibold text-slate-900">
                            Станок №{machine.number}
                          </p>
                          <p className="mb-1 text-[0.78rem] text-slate-600">
                            {formatMachineOutput(machine)}
                          </p>
                          <p className="mb-2 text-[0.75rem] text-slate-500">
                            Работа {formatHours(machine.working_hours_per_day)} ч/сут · ТО{' '}
                            {formatHours(machine.maintenance_hours_per_day)} ч
                          </p>
                          <div
                            className="mt-auto h-2 overflow-hidden rounded bg-slate-200"
                            role="meter"
                            aria-valuemin={0}
                            aria-valuemax={100}
                            aria-valuenow={availability}
                            aria-label={`Доступность станка №${machine.number} линии «${line.label}»: ${availability} процентов`}
                          >
                            <div
                              className="h-full min-w-0 rounded bg-gradient-to-r from-[var(--machine-accent,#2563eb)] to-blue-800 transition-[width] duration-500 ease-out"
                              style={{ width: `${availability}%` }}
                            />
                          </div>
                          <p className="mb-0 mt-1.5 text-[0.78rem] text-slate-500">
                            Доступность {availability}%
                          </p>
                        </div>
                      </article>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {(showPlanning || showProductionRequests) && (
        <section
          className="mb-6 grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-4"
          aria-label="Сводка по производству на сегодня"
        >
          {showPlanning && (
            <>
              <div className={cn(cardClass, 'min-h-[120px]')}>
                <h2 className="m-0 mb-1.5 text-base font-semibold text-su-text-strong">
                  Слоты на сегодня
                </h2>
                {scheduleLoading ? (
                  <p className="m-0 text-[0.88rem] text-gray-400">Загрузка…</p>
                ) : (
                  <>
                    <p className="m-0 text-2xl font-semibold text-su-text-strong">{todaySlots.length}</p>
                    <p className="mt-1 mb-0 text-[0.82rem] text-gray-500">
                      В работе {planFact.inProgress} · Завершено {planFact.completed}
                    </p>
                    <Link to="/planning/schedule" className="mt-2 inline-block text-sm text-blue-700 underline">
                      Открыть график
                    </Link>
                  </>
                )}
              </div>
              <div className={cn(cardClass, 'min-h-[120px]')}>
                <h2 className="m-0 mb-1.5 text-base font-semibold text-su-text-strong">План / факт</h2>
                {scheduleLoading ? (
                  <p className="m-0 text-[0.88rem] text-gray-400">Загрузка…</p>
                ) : (
                  <>
                    <p className="m-0 text-2xl font-semibold text-su-text-strong">
                      {formatQty(planFact.actual)} / {formatQty(planFact.planned)}
                    </p>
                    <p className="mt-1 mb-0 text-[0.82rem] text-gray-500">Выработка за сегодня, шт.</p>
                    <Link to="/planning/output" className="mt-2 inline-block text-sm text-blue-700 underline">
                      Отчёт выработки
                    </Link>
                  </>
                )}
              </div>
            </>
          )}
          {showProductionRequests && (
            <div className={cn(cardClass, 'min-h-[120px]')}>
              <h2 className="m-0 mb-1.5 text-base font-semibold text-su-text-strong">Просроченные строки</h2>
              {scheduleLoading ? (
                <p className="m-0 text-[0.88rem] text-gray-400">Загрузка…</p>
              ) : (
                <>
                  <p className="m-0 text-2xl font-semibold text-red-700">{overdueLines.length}</p>
                  <p className="mt-1 mb-0 text-[0.82rem] text-gray-500">Строк заявок с прошедшим планом производства</p>
                  <Link to="/planning/queue" className="mt-2 inline-block text-sm text-blue-700 underline">
                    Очередь планирования
                  </Link>
                </>
              )}
            </div>
          )}
        </section>
      )}

      {showPlanning && todaySlots.length > 0 && !scheduleLoading && (
        <section className={cn(cardClass, 'mb-6 px-5 py-5')} aria-label="Ближайшие слоты">
          <h2 className="m-0 mb-3 text-[1.05rem] font-semibold text-su-text-strong">Слоты на сегодня</h2>
          <ul className="m-0 list-none space-y-2 p-0">
            {todaySlots.slice(0, 6).map((slot) => (
              <li
                key={slot.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-su-border bg-su-surface-muted px-3 py-2 text-sm"
              >
                <span className="font-medium text-su-text-strong">
                  {slot.expand?.requestLine?.expand?.product?.name ?? 'Слот'}
                </span>
                <span className="text-su-text">
                  {SCHEDULE_SLOT_STATUS_LABELS[slot.status]} · {formatQty(slot.plannedQuantity)} шт.
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section
        className="grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-4"
        aria-label="Заглушка виджетов"
      >
        <div className={cn(cardClass, 'min-h-[120px] transition hover:shadow-su-md')}>
          <h2 className="m-0 mb-1.5 text-base font-semibold text-su-text-strong">Склады / остатки</h2>
          <p className="m-0 text-[0.88rem] text-gray-500">
            {can(PERMISSION_KEYS.INVENTORY_READ) || can(PERMISSION_KEYS.FINISHED_GOODS_READ) ? (
              <Link to="/inventory" className="text-blue-700 underline">
                Перейти на склад
              </Link>
            ) : (
              'Нет доступа к складу'
            )}
          </p>
        </div>
      </section>
    </div>
  )
}
